import React from 'react';
import { useIntl } from 'react-intl';
import Helmet from 'react-helmet';

// Generic
import { RedirectType, WelcomeError } from '../../../types';
import SocialLoginPanel from '../../../components/SocialLoginPanel';
import Whitebox from '../../../components/WhiteboxPage';

// Welcome Components
import { FooterLogoWrapper } from './styled';
import {
  WelcomeForm,
  WelcomePrivacyPolicy,
  WelcomeRateLimitExceededError,
  WelcomeRecaptchaNotice,
} from './components';
import messages from './BasePage.messages';

// Utils
import { WelcomeHeader } from './components/WelcomeHeader';
import { useWelcomePageActions, useWelcomeSubmission, useWelcomeState } from './hooks';
import { WELCOME_PAGE_ID } from './constants';
import { useButtonClickedEvent } from '../../../utilities/analytics/analytics-web-client';
import { fetchPageState } from '../../../utilities/hooks/useGlobalPageState';
import { AidFrontendFeatureGate } from '../../../providers/FeatureGates';
import { checkFeatureGateOnceInitialized } from '../../../utilities/feature-flags/feature-gate';
import { useMicrobranding } from '../../../utilities/hooks/useMicrobranding';

// Add the loader function before the component
export async function welcomeLoader({ request }: { request: Request }) {
  if (!(await checkFeatureGateOnceInitialized(AidFrontendFeatureGate.USE_STATE_DATA_LOADERS))) {
    return null;
  }

  return await fetchPageState('/rest/signup/welcome/state', request, {
    capabilities: ['capability-group--signup', 'capability-name--signup'],
  });
}

export const WelcomePage = () => {
  const { formatMessage } = useIntl();

  const { isEmbedded } = useMicrobranding();

  const { displayName, email } = useWelcomeState();

  const { onWelcomeSubmit, isLoading, formError, fieldErrors, error, redirectType } =
    useWelcomeSubmission();

  const onWelcomeClick = useButtonClickedEvent(WELCOME_PAGE_ID, 'welcomeSignup', {
    redirectType,
  });

  const { handleAppleLogin, handleGoogleLogin, handleMicrosoftLogin, handleSlackLogin } =
    useWelcomePageActions();

  if (error === WelcomeError.RateLimitExceeded) {
    return <WelcomeRateLimitExceededError />;
  }

  return (
    <Whitebox
      pageId={WELCOME_PAGE_ID}
      header={<WelcomeHeader />}
      footer={
        <FooterLogoWrapper>
          <WelcomeRecaptchaNotice />
        </FooterLogoWrapper>
      }>
      <Helmet title={formatMessage(messages.subheadingResumeSignup)} />
      <WelcomeForm
        redirectType={redirectType}
        fieldErrors={fieldErrors}
        formError={formError}
        isLoading={isLoading}
        onSubmit={onWelcomeSubmit}
        onWelcomeClick={onWelcomeClick}
        email={email}
        displayName={displayName}
        privacyPolicy={<WelcomePrivacyPolicy />}
      />
      {isEmbedded
        ? null
        : redirectType === RedirectType.Migration && (
            <SocialLoginPanel
              handleGoogleLogin={handleGoogleLogin}
              handleMicrosoftLogin={handleMicrosoftLogin}
              handleAppleLogin={handleAppleLogin}
              handleSlackLogin={handleSlackLogin}
            />
          )}
    </Whitebox>
  );
};

export default WelcomePage;
